import ky from 'ky';

import {
  AUTH_EXPIRED_EVENT,
  CSRF_COOKIE_NAME,
  CSRF_HEADER_NAME,
} from '@/config/constants.js';
import { env } from '@/config/env.js';
import { readCookie } from '@/utils/cookies.js';

/**
 * Pure hook — sets the CSRF header on an outgoing ky Request from the
 * provided token source. Exported for direct testing.
 *
 * ky v2 calls hooks with a single `{request, options, retryCount}` object.
 *
 * @param {() => string | null} getCsrfToken
 */
export function buildCsrfBeforeRequestHook(getCsrfToken) {
  return function csrfBeforeRequest({ request }) {
    const csrf = getCsrfToken();
    if (csrf) request.headers.set(CSRF_HEADER_NAME, csrf);
    return request;
  };
}

/**
 * Pure hook — invokes onAuthExpired when the backend returns 401.
 * Exported for direct testing.
 *
 * ky v2 calls hooks with a single `{request, options, response, retryCount}` object.
 *
 * @param {() => void} onAuthExpired
 */
export function buildAuthExpiredAfterResponseHook(onAuthExpired) {
  return function authExpiredAfterResponse({ response }) {
    if (response.status === 401) onAuthExpired();
  };
}

/**
 * Pure hook — replaces ky's default HTTPError.message ("Request failed with
 * status code 401 Unauthorized: GET <url>") with the backend's own `{error}`
 * or `{message}` field so toasts and inline errors show something meaningful.
 *
 * ky v2 calls `beforeError` hooks with a `{request, options, error, retryCount}`
 * state object — NOT the bare error — and pre-parses the response body into
 * `error.data` before the hook runs (the body stream is already consumed, so
 * `error.response.json()` no longer works). JSON bodies land on `error.data`
 * as an object; other content types as plain text. Call sites can read
 * `error.data` directly for structured hints (action_text, url, reauth_required).
 * Exported for direct testing.
 */
export function backendErrorMessageBeforeErrorHook({ error }) {
  let body = error?.data;
  // ky parses a JSON body into an object only when the response carries a
  // JSON Content-Type; otherwise `error.data` arrives as a raw string. Some
  // backend error paths (e.g. auth middleware) reply with a JSON body but a
  // non-JSON Content-Type — parse those too so their message still surfaces.
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      // Genuinely not JSON (HTML error page, plain text) — keep ky's message.
    }
  }
  if (body && typeof body === 'object') {
    const msg = body.error ?? body.message;
    if (msg) error.message = msg;
  }
  return error;
}

/** Default behavior when a 401 is observed — fires a window event. */
function defaultOnAuthExpired() {
  window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
}

/**
 * Factory — produces a configured ky instance. Kept as a factory so the
 * app-wide singleton and unit tests share construction logic with
 * explicit dependency injection for CSRF and the auth-expired sink.
 *
 * @param {object} opts
 * @param {string} opts.baseUrl
 * @param {() => string | null} opts.getCsrfToken
 * @param {() => void} [opts.onAuthExpired]
 */
export function createApiClient({
  baseUrl,
  getCsrfToken,
  onAuthExpired = defaultOnAuthExpired,
}) {
  return ky.create({
    prefix: baseUrl,
    credentials: 'include',
    hooks: {
      beforeRequest: [buildCsrfBeforeRequestHook(getCsrfToken)],
      afterResponse: [buildAuthExpiredAfterResponseHook(onAuthExpired)],
      beforeError: [backendErrorMessageBeforeErrorHook],
    },
    retry: {
      limit: 2,
      methods: ['get'],
      statusCodes: [502, 503, 504],
    },
  });
}

/** App-wide default client — built once from env + browser cookies. */
export const apiClient = createApiClient({
  baseUrl: env.BACKEND_URL,
  getCsrfToken: () => readCookie(CSRF_COOKIE_NAME),
});
